// Sheets' comments adapter. A new comment anchors to the active cell / range
// ("Sheet1!B4", "'Q3 plan'!B4:C6") with its values as the quote; clicking a
// thread goes to that sheet and selects the range. The editor keeps the current
// anchor up to date (peekAnchor), so capturing one is synchronous.

import type { CommentsAdapter } from "@/features/drive-file/CommentsPanel";
import type { WorkbookEditorHandle } from "@/features/workbook/WorkbookEditor";
import { parseRef } from "@/features/workbook/geometry";

/** "Sheet1!B4" or "'Q3 plan'!B4:C6" -> sheet name and range text; null when it isn't one. */
function splitAnchor(anchor: string): { sheet: string; range: string } | null {
    const bang = anchor.lastIndexOf("!");
    if (bang <= 0) return null;
    let sheet = anchor.slice(0, bang);
    if (sheet.length >= 2 && sheet.startsWith("'") && sheet.endsWith("'")) sheet = sheet.slice(1, -1).replace(/''/g, "'");
    const range = anchor.slice(bang + 1).trim().toUpperCase().replace(/\$/g, "");
    return parseRef(range) ? { sheet, range } : null;
}

export function sheetCommentsAdapter(getEditor: () => WorkbookEditorHandle | null): CommentsAdapter {
    return {
        selectionHint: "Select a cell or range to comment on it.",

        captureAnchor: () => getEditor()?.peekAnchor() ?? null,

        describeAnchor: (anchor) => {
            const parts = splitAnchor(anchor);
            return parts ? `${parts.sheet}!${parts.range}` : null;
        },

        navigate: (anchor) => {
            const editor = getEditor();
            if (!editor || !anchor || !splitAnchor(anchor)) return false;
            return editor.goTo(anchor);
        }
    };
}
